import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useEffect} from 'react';

import {DomainGraphType, ObjectType} from 'app-core/framework/CoreObjectConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SeedItems} from 'pipelines/generalized-step-config-editor/editors/custom-editors/SeedItemsEditor';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {ResultSchemaSelectorIdInput} from 'results/ResultSchemaSelector';
import AllStorablesSchemaSelector from 'samples/AllStorablesSchemaSelector';
import {isSchemaTypeFilterOption} from 'schemas/SchemaConstants';
import QueryingTagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import SearchInput from 'search/inputs/SearchInput';
import {FONT_FAMILY_PROXIMA, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';

type DatasetConfig = PipelineStepConfig<
  PipelineStepType.CREATE_PROCEDURE_DATASET,
  0,
  'draft'
>['datasetConfig'];
type ProcedureDatasetType = NonNullable<DatasetConfig>['procedureDatasetType'];
type DatasetConfigWithSourceObject = NonNullable<DatasetConfig> & {sourceObjectId: string | null};
type SourceFieldShape = {kind: 'domain'; types: ReadonlyArray<string>} | {kind: 'string'};

const STEP_SHAPE = PIPELINE_STEP_SHAPES[PipelineStepType.CREATE_PROCEDURE_DATASET][0];
const DATASET_CONFIG_SHAPE = STEP_SHAPE.config.datasetConfig;

function getSourceObjectType(type: ProcedureDatasetType): ObjectType | 'string' | null {
  const choice = DATASET_CONFIG_SHAPE.choices.find((c) => c.fields.procedureDatasetType.value === type);
  if (choice == null) {
    return null;
  }
  const fields = choice.fields as typeof choice.fields & {sourceObjectId?: SourceFieldShape};
  const sourceField = fields.sourceObjectId;
  if (sourceField == null) {
    return null;
  }
  if (sourceField.kind === 'string') {
    return 'string';
  }
  const {types} = sourceField;
  if (types.length !== 1 || !isObjectType(types[0])) {
    return null;
  }
  return types[0];
}

function hasSourceObjectId(config: NonNullable<DatasetConfig>): config is DatasetConfigWithSourceObject {
  return getSourceObjectType(config.procedureDatasetType) != null;
}

function isObjectType(value: string): value is ObjectType {
  return Object.hasOwn(DomainGraphType, value);
}

const DATA_SOURCE_TYPE_DISPLAY_NAMES: Record<ProcedureDatasetType, string> = {
  RESULT_SCHEMA: 'Result schema',
  ENTITY_SCHEMA: 'Entity schema',
  CONDITIONS: 'Conditions',
  DATA_PARAMETER: 'Data parameter',
  MATERIAL_INPUT: 'Material input',
  MATERIAL_OUTPUT: 'Material output',
  EQUIPMENT: 'Equipment',
  TEST_ORDERS: 'Test orders',
  PREVIOUS_TEST_ORDERS: 'Previous test orders',
  PMEI_ASSOCIATION: 'Unit operations links',
  INDIVIDUAL_PLATE_POSITIONS: 'Individual plate positions',
  INDIVIDUAL_PLATE_CONTENTS: 'Individual plate contents',
  PLATE_SUMMARY: 'Plate summary',
  PLATE_RECORD_POSITIONS: 'Plate record positions',
  PLATE_RECORD_CONTENTS: 'Plate record contents',
  PLATE_DESIGN_POSITIONS: 'Plate design positions',
  PLATE_DESIGN_CONTENTS: 'Plate design contents',
};

// Dataset types that are incompatible with WORKSHEET seed items.
const WORKSHEET_EXCLUDED_TYPES: ReadonlySet<ProcedureDatasetType> = new Set(['PMEI_ASSOCIATION']);

function getDatasetTypeDisabledMessage(
  datasetType: ProcedureDatasetType,
  seedItemType: NonNullable<SeedItems>['seedItemType']
): string | undefined {
  if (WORKSHEET_EXCLUDED_TYPES.has(datasetType) && seedItemType === 'WORKSHEET') {
    return 'Unit operations links are not available for worksheets';
  }
  return undefined;
}

function isProcedureDatasetType(value: string): value is ProcedureDatasetType {
  return Object.hasOwn(DATA_SOURCE_TYPE_DISPLAY_NAMES, value);
}

export function DataSourceTypeEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const datasetConfig = config.datasetConfig as DatasetConfig | null;
  const currentType = datasetConfig?.procedureDatasetType ?? null;
  const currentVersion = datasetConfig?.procedureDatasetVersion ?? 1; // Default to version 1 if not specified
  const seedItemType = (config.seedItems as SeedItems | null)?.seedItemType ?? 'STUDY';

  useEffect(() => {
    if (currentType != null && getDatasetTypeDisabledMessage(currentType, seedItemType) != null) {
      onChangeConfig(config, ['datasetConfig'], null);
    }
  }, [seedItemType, currentType, onChangeConfig, config]);

  const menuOptions = DATASET_CONFIG_SHAPE.choices.map((choice) => {
    const procedureDatasetType = choice.fields.procedureDatasetType.value;
    const disabledMessage = getDatasetTypeDisabledMessage(procedureDatasetType, seedItemType);
    return {
      name: DATA_SOURCE_TYPE_DISPLAY_NAMES[procedureDatasetType],
      value: procedureDatasetType,
      disabled: disabledMessage != null,
      disabledMessage,
    };
  });

  const handleSelect = (value: string): void => {
    if (!isProcedureDatasetType(value) || value === currentType) {
      return;
    }
    onChangeConfig(config, ['datasetConfig'], {
      procedureDatasetType: value,
      procedureDatasetVersion: currentVersion,
      ...(getSourceObjectType(value) != null ? {sourceObjectId: null} : {}),
    });
  };

  return (
    <div className={css(styles.section)}>
      <div className={css(styles.sectionTitle)}>Select data source type</div>
      <SelectDropdown
        dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', key: 'dataSourceType'}}
        displayName={
          currentType != null ? DATA_SOURCE_TYPE_DISPLAY_NAMES[currentType] : 'Select data source type'
        }
        buttonSize={ButtonSize.SMALL}
        menu={
          <SearchableMenu
            className="menu--short"
            width={Menu.WIDTHS.FULL}
            optionDatas={menuOptions}
            selectedValue={currentType}
            onSelect={handleSelect}
          />
        }
        disabled={disabled}
      />
    </div>
  );
}

export function DataSourceEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const datasetConfig = config.datasetConfig as DatasetConfig | null;
  const currentType = datasetConfig?.procedureDatasetType ?? null;
  const sourceObjectType = currentType != null ? getSourceObjectType(currentType) : null;

  if (datasetConfig == null || sourceObjectType == null || !hasSourceObjectId(datasetConfig)) {
    return (
      <div className={css(styles.section, styles.sectionSpacing)}>
        <div className={css(styles.sectionTitle)}>Select data source</div>
        <div className={css(styles.placeholder)}>
          {currentType == null
            ? 'Select a data source type first'
            : 'Not applicable for this data source type'}
        </div>
      </div>
    );
  }

  const sourceObjectId = datasetConfig.sourceObjectId ?? null;

  return (
    <div className={css(styles.section, styles.sectionSpacing)}>
      <div className={css(styles.sectionTitle)}>Select data source</div>
      {renderSourceSelector({sourceObjectType, sourceObjectId, onChangeConfig, config, disabled})}
    </div>
  );
}

function renderSourceSelector({
  sourceObjectType,
  sourceObjectId,
  onChangeConfig,
  config,
  disabled,
}: {
  sourceObjectType: ObjectType | 'string';
  sourceObjectId: string | null;
  onChangeConfig: CustomComponentEditorProps['onChangeConfig'];
  config: CustomComponentEditorProps['config'];
  disabled?: boolean;
}): JSX.Element {
  const handleSourceChange = (id: string | null): void => {
    onChangeConfig(config, ['datasetConfig', 'sourceObjectId'], id);
  };

  if (sourceObjectType === 'string') {
    return (
      <input
        className={css(styles.fullWidth, styles.readOnlyInput)}
        value={sourceObjectId ?? ''}
        readOnly={true}
        placeholder="Source object ID"
      />
    );
  } else if (sourceObjectType === 'ENTITY_SCHEMA') {
    return (
      <QueryingTagSchemaSelector
        selectedTagSchemaId={sourceObjectId}
        onChange={(tagSchema) => {
          handleSourceChange(tagSchema?.id ?? null);
        }}
        isDisabled={disabled}
      />
    );
  } else if (sourceObjectType === 'RESULT_SCHEMA') {
    return (
      <ResultSchemaSelectorIdInput
        selectedSchemaId={sourceObjectId}
        onChange={(schema) => {
          handleSourceChange(schema?.id ?? null);
        }}
        isDisabled={disabled}
        shouldShowNoSchemaOption={false}
      />
    );
  } else if (sourceObjectType === 'PLATE_SCHEMA') {
    return (
      <AllStorablesSchemaSelector
        includeStorableTypes={[ModelTypeConstants.PLATE]}
        selectedAllStorablesSchemaId={sourceObjectId}
        onChange={(schema) => {
          handleSourceChange(!isSchemaTypeFilterOption(schema) ? schema.id : null);
        }}
        shouldShowNoSchemaOption={false}
        isDisabled={disabled}
      />
    );
  } else if (sourceObjectType === 'PLATE') {
    return (
      <SearchInput
        autoFocus={false}
        disabled={disabled}
        disableTyping={sourceObjectId != null}
        isMulti={false}
        onRemove={() => {
          handleSourceChange(null);
        }}
        onSelect={handleSourceChange}
        placeholder="Search for a plate..."
        selected={sourceObjectId == null ? [] : [sourceObjectId]}
        types={[ModelTypeConstants.PLATE]}
      />
    );
  } else {
    assert(false, `Unexpected source object type: ${sourceObjectType}`);
  }
}

const styles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  sectionSpacing: {
    marginTop: 16,
  },
  sectionTitle: {
    fontFamily: FONT_FAMILY_PROXIMA,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    fontSize: 13,
    lineHeight: '16px',
    letterSpacing: 0,
    marginBottom: 8,
  },
  fullWidth: {
    width: '100%',
  },
  placeholder: {
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
  readOnlyInput: {
    background: 'var(--background-secondary, #f5f5f5)',
    border: '1px solid var(--border-default, #ccc)',
    borderRadius: 4,
    color: 'var(--text-secondary)',
    fontSize: 14,
    padding: '6px 8px',
  },
});
